import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { ChevronLeft, ChevronRight, PanelLeft } from "lucide-react";
import { cn } from "@/lib/utils";

const LS_LEFT = "prometheus_os_left_collapsed_v1";
const LS_PANEL_W = "prometheus_os_panel_width_v1";

const PANEL_MIN = 300;
const PANEL_MAX = 720;
const PANEL_DEFAULT = 440;

export type OsShellProps = {
  sidebar: ReactNode;
  main: ReactNode;
  panel?: ReactNode;
  className?: string;
};

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(LS_LEFT) === "1";
  } catch {
    return false;
  }
}

function readPanelWidth(): number {
  try {
    const n = Number(localStorage.getItem(LS_PANEL_W));
    if (Number.isFinite(n) && n >= PANEL_MIN && n <= PANEL_MAX) return n;
  } catch {
    /* ignore */
  }
  return PANEL_DEFAULT;
}

/**
 * Chat OS layout under fixed Nav.
 * Left sidebar: collapsible (hidden rail on mobile; thin rail on md+).
 * Right Artifact: drag-resizable width.
 */
export function OsShell({ sidebar, main, panel, className }: OsShellProps) {
  const [leftCollapsed, setLeftCollapsed] = useState(readCollapsed);
  const [panelWidth, setPanelWidth] = useState(readPanelWidth);
  const dragging = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(LS_LEFT, leftCollapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [leftCollapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_PANEL_W, String(panelWidth));
    } catch {
      /* ignore */
    }
  }, [panelWidth]);

  /** Chat header + panels can open/toggle/close the drawer via window events. */
  useEffect(() => {
    const open = () => setLeftCollapsed(false);
    const close = () => setLeftCollapsed(true);
    const toggle = () => setLeftCollapsed((c) => !c);
    const publish = () => {
      window.dispatchEvent(
        new CustomEvent("prometheus-os-sidebar", { detail: { collapsed: leftCollapsed } })
      );
    };
    const onPing = () => publish();
    window.addEventListener("prometheus-os-open-sidebar", open);
    window.addEventListener("prometheus-os-close-sidebar", close);
    window.addEventListener("prometheus-os-toggle-sidebar", toggle);
    window.addEventListener("prometheus-os-sidebar-ping", onPing);
    publish();
    return () => {
      window.removeEventListener("prometheus-os-open-sidebar", open);
      window.removeEventListener("prometheus-os-close-sidebar", close);
      window.removeEventListener("prometheus-os-toggle-sidebar", toggle);
      window.removeEventListener("prometheus-os-sidebar-ping", onPing);
    };
  }, [leftCollapsed]);

  const onResizePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragging.current = true;
    const startX = e.clientX;
    const startW = panelWidth;

    const onMove = (ev: PointerEvent) => {
      if (!dragging.current) return;
      const delta = startX - ev.clientX; // drag left = wider panel
      const next = Math.min(PANEL_MAX, Math.max(PANEL_MIN, startW + delta));
      setPanelWidth(next);
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [panelWidth]);

  return (
    <div
      className={cn(
        "relative flex w-full h-[calc(100dvh-3.5rem)] mt-14 overflow-hidden bg-void border-t border-border sm:mt-16 sm:h-[calc(100dvh-4rem)]",
        className
      )}
    >
      {/* Mobile dimmer to the right of the drawer (does not cover the panel) */}
      {!leftCollapsed ? (
        <button
          type="button"
          className="fixed top-14 bottom-0 right-0 z-30 bg-black/45 backdrop-blur-[1px] md:hidden cursor-pointer sm:top-16"
          style={{ left: "min(272px, 88vw)" }}
          aria-label="Cerrar menú"
          onClick={() => setLeftCollapsed(true)}
        />
      ) : null}

      {/* Left personal sidebar — full width drawer on mobile; collapsible rail on md+ */}
      <aside
        className={cn(
          "h-full border-r border-border bg-card/95 md:bg-card/40 transition-[width,transform] duration-200 ease-out shrink-0",
          leftCollapsed
            ? "w-0 border-transparent overflow-hidden md:w-11 md:border-border md:overflow-visible"
            : "fixed top-14 bottom-0 left-0 z-40 w-[min(272px,88vw)] shadow-2xl sm:top-16 md:relative md:inset-auto md:top-auto md:bottom-auto md:z-auto md:w-[220px] md:shadow-none lg:w-[260px]"
        )}
      >
        {leftCollapsed ? (
          <div className="hidden md:flex flex-col items-center gap-2 py-3 h-full">
            <button
              type="button"
              onClick={() => setLeftCollapsed(false)}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 cursor-pointer"
              title="Mostrar sidebar"
              aria-label="Mostrar sidebar"
            >
              <PanelLeft className="w-4 h-4" />
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
          </div>
        ) : (
          <div className="relative h-full min-h-0 overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">{sidebar}</div>
            <button
              type="button"
              onClick={() => setLeftCollapsed(true)}
              className="absolute top-2 right-1 z-10 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 cursor-pointer"
              title="Ocultar sidebar"
              aria-label="Ocultar sidebar"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </aside>

      <main className="relative flex-1 min-w-0 h-full overflow-hidden flex flex-col">
        {/* Mobile reopen: ChatWidget only mounts on Chats — without this, Universidad/etc. trap you */}
        {leftCollapsed ? (
          <button
            type="button"
            onClick={() => setLeftCollapsed(false)}
            className="md:hidden absolute top-2 left-2 z-30 flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-card/95 text-foreground shadow-md backdrop-blur-sm hover:border-primary/40 hover:bg-muted/60 cursor-pointer"
            title="Abrir menú"
            aria-label="Abrir menú (chats, proyectos, universidad…)"
          >
            <PanelLeft className="w-4 h-4" />
          </button>
        ) : null}
        {main}
      </main>

      {panel ? (
        <aside
          className="relative hidden md:flex h-full border-l border-border bg-card/30 shrink-0 flex-col min-w-0"
          style={{ width: panelWidth }}
        >
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Ajustar ancho del Artifact"
            onPointerDown={onResizePointerDown}
            className="absolute left-0 top-0 bottom-0 w-1.5 -ml-0.5 z-20 cursor-col-resize group hover:bg-primary/40 active:bg-primary/60"
          >
            <span className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-border group-hover:bg-primary/80" />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pl-1 scrollbar-subtle">{panel}</div>
        </aside>
      ) : null}
    </div>
  );
}
